export interface CustomerNavigationDestination {
  id: 'account' | 'orders' | 'refunds' | 'relocations' | 'rights' | 'incomingTransfers' | 'outgoingTransfers' | 'received' | 'identity' | 'transfers'
  path: `/account${string}`
  labelKey: string
  icon: string
  match?: 'prefix'
  query?: Readonly<Record<string, string>>
  activeViews?: readonly string[]
}

export const CUSTOMER_ACCOUNT_DESTINATION = {
  id: 'account',
  path: '/account',
  labelKey: 'customer.rights.account',
  icon: 'i-lucide-user-round'
} as const satisfies CustomerNavigationDestination

export const CUSTOMER_RIGHTS_DESTINATION = {
  id: 'rights',
  path: '/account/rights',
  labelKey: 'customer.rights.navigation',
  icon: 'i-lucide-ticket-check',
  match: 'prefix',
  query: { view: 'active' },
  activeViews: ['active', 'expired', 'transferred', 'used_revoked']
} as const satisfies CustomerNavigationDestination

export const CUSTOMER_ORDERS_DESTINATION = { id: 'orders', path: '/account/orders', labelKey: 'customer.orders.navigation', icon: 'i-lucide-receipt-text', match: 'prefix' } as const satisfies CustomerNavigationDestination

/** Account menu: the two daily destinations stay visible; the rest folds into one-open-at-a-time sections. */
export const CUSTOMER_MENU_PRIMARY = [CUSTOMER_RIGHTS_DESTINATION, CUSTOMER_ORDERS_DESTINATION] as const satisfies readonly CustomerNavigationDestination[]

export const CUSTOMER_MENU_SECTIONS = [
  {
    id: 'transfers',
    labelKey: 'customer.navigation.transfers',
    icon: 'i-lucide-arrow-left-right',
    destinations: [
      { id: 'incomingTransfers', path: '/account/rights', labelKey: 'customer.rights.navigationIncomingTransfers', icon: 'i-lucide-mail-question', match: 'prefix', query: { view: 'incoming_pending' } },
      { id: 'outgoingTransfers', path: '/account/rights', labelKey: 'customer.rights.navigationOutgoingTransfers', icon: 'i-lucide-send', match: 'prefix', query: { view: 'outgoing_pending' } },
      { id: 'received', path: '/account/rights', labelKey: 'customer.rights.navigationReceived', icon: 'i-lucide-inbox', match: 'prefix', query: { view: 'received' } }
    ]
  },
  {
    id: 'requests',
    labelKey: 'customer.navigation.requests',
    icon: 'i-lucide-life-buoy',
    destinations: [
      { id: 'refunds', path: '/account/refunds', labelKey: 'customer.refunds.navigation', icon: 'i-lucide-clipboard-list', match: 'prefix' },
      { id: 'relocations', path: '/account/relocations', labelKey: 'customer.relocations.navigation', icon: 'i-lucide-map-pinned', match: 'prefix' }
    ]
  },
  {
    id: 'profile',
    labelKey: 'customer.navigation.profile',
    icon: 'i-lucide-user-round-cog',
    destinations: [CUSTOMER_ACCOUNT_DESTINATION, { id: 'identity', path: '/account/identity', labelKey: 'customer.identity.navigation', icon: 'i-lucide-badge-check' }]
  }
] as const satisfies readonly { id: string; labelKey: string; icon: string; destinations: readonly CustomerNavigationDestination[] }[]

/** Bottom bar: rights sits in the middle slot and transfers groups every transfer view of the wallet. */
export const CUSTOMER_MOBILE_DESTINATIONS = [
  CUSTOMER_ORDERS_DESTINATION,
  CUSTOMER_RIGHTS_DESTINATION,
  { id: 'transfers', path: '/account/rights', labelKey: 'customer.navigation.transfers', icon: 'i-lucide-arrow-left-right', match: 'prefix', query: { view: 'incoming_pending' }, activeViews: ['incoming_pending', 'outgoing_pending', 'received'] },
  CUSTOMER_ACCOUNT_DESTINATION
] as const satisfies readonly CustomerNavigationDestination[]

export function customerNavigationTarget(
  destination: CustomerNavigationDestination,
  localePath: (path: string) => string
) {
  const path = localePath(destination.path)
  return destination.query ? { path, query: destination.query } : path
}

export function isCustomerAccountRoute(
  route: { path: string },
  localePath: (path: string) => string
) {
  const accountPath = localePath(CUSTOMER_ACCOUNT_DESTINATION.path)
  return route.path === accountPath || route.path.startsWith(`${accountPath}/`)
}

export function isCustomerNavigationActive(
  route: { path: string, query: Readonly<Record<string, unknown>> },
  destination: CustomerNavigationDestination,
  localePath: (path: string) => string
) {
  const targetPath = localePath(destination.path)
  const pathMatches = route.path === targetPath
    || (destination.match === 'prefix' && route.path.startsWith(`${targetPath}/`))
  if (!pathMatches) return false
  if (destination.activeViews) {
    // The wallet opens on its active view when the URL has none.
    const view = route.query.view ?? 'active'
    return typeof view === 'string' && destination.activeViews.includes(view)
  }
  return !destination.query
    || Object.entries(destination.query).every(([key, value]) => route.query[key] === value)
}
